import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  useCalendarData,
  IUseCalendarDataReturn,
} from 'src/pages/Calendar/CustomHooks/useCalendarData';
import {
  EventTypeStyle,
  getEventTypeStyle,
} from 'src/utils/BigCalendarColourGeneratorUtils';
import {
  ICalendarEvent,
  IProperty,
  IPropertyWithState,
  IUniqueEventType,
  KEY_PICKUP_EVENT_TYPE,
  SERVICE_REQUEST_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';

const eventTypeIdToKey = (eventTypeId: number): string => {
  if (eventTypeId === WALKTHROUGH_EVENT_TYPE) {
    return 'walkthrough';
  }

  if (eventTypeId === KEY_PICKUP_EVENT_TYPE) {
    return 'key-pickup';
  }

  return 'service';
};

const eventTypeKeyToId = (eventTypeKey: string): number => {
  if (eventTypeKey === 'walkthrough') {
    return WALKTHROUGH_EVENT_TYPE;
  }

  if (eventTypeKey === 'key-pickup') {
    return KEY_PICKUP_EVENT_TYPE;
  }

  return SERVICE_REQUEST_EVENT_TYPE;
};

export interface IUseCalendarViewOrchestratorReturn {
  fromDate: Date;
  toDate: Date;
  updateFromDate: (value: Date) => void;
  updateToDate: (value: Date) => void;
  goToToday: () => void;
  isTodaySelected: boolean;
  minDate: Date;
  maxDate: Date;
  defaultDate: Date;

  events: ICalendarEvent[];
  eventTypes: IUniqueEventType[];
  properties: IPropertyWithState[];
  isLoading: boolean;
  error: string | null;
}

export const useCalendarViewOrchestrator =
  (): IUseCalendarViewOrchestratorReturn => {
    const user: any = useSelector(selectAuthenticatedUser);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const [fromDate, setFromDate] = useState<Date>(
      new Date(currentYear, currentMonth, 1)
    );
    const [toDate, setToDate] = useState<Date>(
      new Date(currentYear, currentMonth + 1, 0)
    );

    const calendarData: IUseCalendarDataReturn = useCalendarData();

    useEffect(() => {
      void calendarData.fetchMetadata();
    }, []);

    useEffect(() => {
      if (!user?.id) {
        return;
      }

      void calendarData.fetchEvents(fromDate, toDate);
    }, [user?.id, fromDate, toDate]);

    const minDate: Date = new Date(fromDate);
    minDate.setHours(0, 0, 0, 0);

    const maxDate: Date = new Date(toDate);
    maxDate.setHours(23, 59, 59, 999);

    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);

    const isTodaySelected: boolean =
      today.toDateString() === toDate.toDateString();

    const defaultDate: Date =
      new Date() >= minDate && new Date() <= maxDate ? new Date() : minDate;

    const updateFromDate = (value: Date): void => {
      setFromDate(value);
    };

    const updateToDate = (value: Date): void => {
      setToDate(value);
    };

    const goToToday = (): void => {
      setToDate(new Date());
    };

    const eventTypes: IUniqueEventType[] = useMemo(() => {
      return calendarData.availableEventTypes.map((eventTypeId: number) => {
        const eventTypeKey: string = eventTypeIdToKey(eventTypeId);
        const style: EventTypeStyle = getEventTypeStyle(eventTypeKey);

        const hasEventsInDateRange: boolean = calendarData.events.some(
          (event: ICalendarEvent) => {
            const eventTypeAsNumber: number =
              typeof event.eventType === 'string'
                ? eventTypeKeyToId(event.eventType)
                : event.eventType;

            return eventTypeAsNumber === eventTypeId;
          }
        );

        return {
          text: style.label,
          value: eventTypeKey,
          color: style.backgroundColor,
          textColor: style.textColor,
          enabled: hasEventsInDateRange,
        };
      });
    }, [calendarData.availableEventTypes, calendarData.events]);

    const properties: IPropertyWithState[] = useMemo(() => {
      return calendarData.availableProperties.map((apiProperty: IProperty) => {
        const hasEventsInDateRange: boolean = calendarData.events.some(
          (event: ICalendarEvent) =>
            event.originalEvent.propertyId === apiProperty.propertyId
        );

        return {
          id: apiProperty.propertyId,
          text: apiProperty.name,
          enabled: hasEventsInDateRange,
        };
      });
    }, [calendarData.availableProperties, calendarData.events]);

    return {
      fromDate,
      toDate,
      updateFromDate,
      updateToDate,
      goToToday,
      isTodaySelected,
      minDate,
      maxDate,
      defaultDate,
      events: calendarData.events,
      eventTypes,
      properties,
      isLoading: calendarData.isLoadingEvents,
      error: calendarData.eventsError,
    };
  };